import { defineComponent as _defineComponent } from 'vue'
import appCan from '@@/bits/app_can'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing, isUsingMediaComments } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import { useAppBarAccountStore } from '@@/pinia/app_bar_account'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useLibraryPlansStore } from '@@/pinia/library_plans'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceActivityPanelStore } from '@@/pinia/surface_activity_panel'
import { useSurfaceAutomationsStore } from '@@/pinia/surface_automations_store'
import { useCommentEditMode, useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDetailsPanelStore } from '@@/pinia/surface_details_panel'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceRemakePanelStore } from '@@/pinia/surface_remake_panel'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceSharePanelStore } from '@@/pinia/surface_share_panel'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Id } from '@@/types'
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'

// IMPORTANT: If you're adding a new panel here, please also add it to the
// `isOverlayVisible` getter in `app/javascript/vuexstore/surface/index.ts`

export default /*#__PURE__*/_defineComponent({
  __name: 'SurfacePanels',
  props: {
    reactionIdsByPostId: { default: () => ({}) },
    commentIdsByPostId: { default: () => ({}) }
  },
  setup(__props: any) {

const SurfaceContributors = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContributors.vue'))
const SurfaceSearchBar = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSearchBar.vue'))
const SurfacePanelMapLocationPicker = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePanelMapLocationPicker.vue'),
)
const SurfaceDetailsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDetailsPanel.vue'))
const SurfaceSettingsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSettingsPanel.vue'))
const SurfaceSharePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSharePanel.vue'))
const SurfacePanelRemake = defineAsyncComponent(() => import('@@/vuecomponents/surface_panel_remake.vue'))
const SurfaceRemakePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceRemakePanel.vue'))
const SurfacePanelPostExpanded = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePanelPostExpanded.vue'))
const SurfacePanelPostActionMenuNew = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePanelPostActionMenu.vue'),
)
const SurfaceSectionActionMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSectionActionMenu.vue'))
const SurfaceCommentActionMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceCommentActionMenu.vue'))
const SurfaceComposerModalList = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceComposerModalList.vue'))
const SurfaceComposerDraftsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceComposerDraftsPanel.vue'))
const SurfacePanelComposerModal = defineAsyncComponent(
  () => import('@@/vuecomponents/surface_panel_composer_modal.vue'),
)
const UpgradeRequired = defineAsyncComponent(() => import('@@/vuecomponents/upgrade_required.vue'))
const SurfaceDropdownReactionStar = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDropdownReactionStar.vue'),
)
const SurfaceDropdownReactionGrade = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDropdownReactionGrade.vue'),
)
const CanvasConnectionSnackbar = defineAsyncComponent(() => import('@@/vuecomponents/CanvasConnectionSnackbar.vue'))
const DialogPermitNotification = defineAsyncComponent(() => import('@@/vuecomponents/DialogPermitNotification.vue'))
const SurfaceInfo = defineAsyncComponent(() => import('@@/vuecomponents/surface_info.vue'))
const SurfaceDialogExtensionRequired = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDialogExtensionRequired.vue'),
)
const GlobalConfirmationDialogVuex = defineAsyncComponent(
  () => import('@@/vuecomponents/GlobalConfirmationDialogVuex.vue'),
)
const GlobalConfirmationDialog = defineAsyncComponent(() => import('@@/vuecomponents/GlobalConfirmationDialog.vue'))
const GlobalInputDialog = defineAsyncComponent(() => import('@@/vuecomponents/GlobalInputDialog.vue'))
const GlobalAlertDialog = defineAsyncComponent(() => import('@@/vuecomponents/GlobalAlertDialog.vue'))
const ReloadNotification = defineAsyncComponent(() => import('@@/vuecomponents/ReloadNotification.vue'))
const SurfaceSectionRenameModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSectionRenameModal.vue'))
const SurfaceCommentEditModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceCommentEditModal.vue'))
const SurfaceCommentEditDrawer = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceCommentEditDrawer.vue'))
const MembershipTierUpgradeModal = defineAsyncComponent(() => import('@@/vuecomponents/MembershipTierUpgradeModal.vue'))
const SurfaceEditBookmarkModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceEditBookmarkModal.vue'))
const MembershipLibraryTierUpgradeModal = defineAsyncComponent(
  () => import('@@/vuecomponents/MembershipLibraryTierUpgradeModal.vue'),
)
const UserMenu = defineAsyncComponent(() => import('@@/vuecomponents/UserMenu.vue'))
const SurfaceGuestIdCard = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGuestIdCard.vue'))
const SurfaceActivityPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceActivityPanel.vue'))
const SurfaceReportPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceReportPanel.vue'))
const SurfaceDevelopersPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDevelopersPanel.vue'))
const SurfaceFreezePadletPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceFreezePadletPanel.vue'))
const SurfaceQrCodeModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceQrCodeModal.vue'))
const SurfaceAutomationsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAutomationsPanel.vue'))
const SurfacePanelCopyOrTransferPost = defineAsyncComponent(
  () => import('@@/vuecomponents/surface_panel_copy_or_transfer_post.vue'),
)



const { shouldEditCommentsInModal } = useCommentEditMode()

const isApp = device.app

const surfaceStore = useSurfaceStore()
const {
  xInfo,
  xWallQuotaExceeded,
  xExtensionRequired,
  fileThatExceededSize,
  xRemakePanelOld,
  isNativeMenu,
  xPermitNotification,
  xReloadNotification,
  isFirstView,
  xSearchBar,
  user,
  isMobileLayout,
  xNewComposerModalPanel,
  canUseSections,
  xReportPanel,
  xDevelopersPanel,
  xSettingsPanel,
  hasSidePanelOutsideSurface,
  xEditBookmarksDialog,
} = storeToRefs(surfaceStore)
const { xDraftsPanel } = storeToRefs(useSurfaceDraftsStore())
const { isPickingLocation } = storeToRefs(useSurfaceMapStore())
const { xContributorsList } = storeToRefs(useSurfaceUserContributorsStore())
const { postUnderActionCid, xTransferPostPanel, postBeingTransferredCid, xCopyPostPanel, postBeingCopiedCid } =
  storeToRefs(useSurfacePostActionStore())
const { xExpandedPostPanel } = storeToRefs(useExpandedPostStore())
const { xFilterModal } = storeToRefs(useSurfaceFilterStore())
const { xUserMenu } = storeToRefs(useAppBarAccountStore())
const { xGuestIdCard } = storeToRefs(useSurfaceGuestStore())
const { xSurfaceSharePanel, xQrModal } = storeToRefs(useSurfaceSharePanelStore())
const { xRemakePanel } = storeToRefs(useSurfaceRemakePanelStore())
const { xUpgradeModal: xLibraryUpgradeModal } = storeToRefs(useLibraryPlansStore())
const { xUpgradeModal } = storeToRefs(usePersonalPlansStore())
const { commentUnderActionId } = storeToRefs(useCommentsStore())
const { sectionUnderActionId, xSectionRenameModal } = storeToRefs(useSurfaceSectionsStore())
const { xConnectionCancellation, xDisconnectionCancellation } = storeToRefs(useSurfacePostConnectionStore())
const { isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())
const { xActivityPanel } = storeToRefs(useSurfaceActivityPanelStore())
const { isContainerSmallerThanTabletPortrait, isContainerSmallerThanTabletLandscape } = storeToRefs(
  useSurfaceContainerSizeStore(),
)
const { xSurfaceFreezePanel } = storeToRefs(useSurfaceFreezePadletPanel())
const { xSurfaceDetailsPanel } = storeToRefs(useSurfaceDetailsPanelStore())
const { xSurfaceAutomationsPanel } = storeToRefs(useSurfaceAutomationsStore())
const { originalReactionData, postIdBeingReactedTo } = storeToRefs(useReactionsStore())
const { amIRegistered } = storeToRefs(useSurfacePermissionsStore())

const { isDemoPadletPanelAndShouldHideUI } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())

const isAppOpeningNativeLocationPicker = computed(() => {
  return isApp && appCan('useNativeMapLocationPicker')
})

const xCurtain = computed(() => {
  if (hasSidePanelOutsideSurface.value && isSmallerThanTabletPortrait.value) return false
  return xSurfaceSharePanel.value || xCopyPostPanel.value || xTransferPostPanel.value
})

const xCommentMenu = computed(() => {
  return commentUnderActionId.value && !isNativeMenu.value
})

onMounted(() => {
  if (isAppUsing('onboardingPanel')) {
    return
  }
  if (isFirstView.value) {
    showSettingsPanel()
  }
})

const { hideExtensionRequired, hideWallQuotaExceeded, hideInfo, hidePermitNotification, hideReloadNotification } =
  surfaceStore
const { endPostTransfer, postTransferNavigate, endPostCopy, postCopyNavigate } = useSurfacePostActionStore()
const { closeUpgradeModal: closeLibraryUpgradeModal } = useLibraryPlansStore()
const { showSettingsPanel } = useSurfaceSettingsStore()

function closePlanUpgradeModal() {
  usePersonalPlansStore().closePlanUpgradeModal()
}

function navigateCopyPanel(panel) {
  postCopyNavigate({ panel })
}

function navigateTransferPanel(panel) {
  postTransferNavigate({ panel })
}

function hideCanvasConnection() {
  if (xConnectionCancellation.value) {
    useSurfacePostConnectionStore().cancelConnection()
  } else if (xDisconnectionCancellation.value) {
    useSurfacePostConnectionStore().cancelDisconnection()
  }
}

return { __sfc: true,SurfaceContributors, SurfaceSearchBar, SurfacePanelMapLocationPicker, SurfaceDetailsPanel, SurfaceSettingsPanel, SurfaceSharePanel, SurfacePanelRemake, SurfaceRemakePanel, SurfacePanelPostExpanded, SurfacePanelPostActionMenuNew, SurfaceSectionActionMenu, SurfaceCommentActionMenu, SurfaceComposerModalList, SurfaceComposerDraftsPanel, SurfacePanelComposerModal, UpgradeRequired, SurfaceDropdownReactionStar, SurfaceDropdownReactionGrade, CanvasConnectionSnackbar, DialogPermitNotification, SurfaceInfo, SurfaceDialogExtensionRequired, GlobalConfirmationDialogVuex, GlobalConfirmationDialog, GlobalInputDialog, GlobalAlertDialog, ReloadNotification, SurfaceSectionRenameModal, SurfaceCommentEditModal, SurfaceCommentEditDrawer, MembershipTierUpgradeModal, SurfaceEditBookmarkModal, MembershipLibraryTierUpgradeModal, UserMenu, SurfaceGuestIdCard, SurfaceActivityPanel, SurfaceReportPanel, SurfaceDevelopersPanel, SurfaceFreezePadletPanel, SurfaceQrCodeModal, SurfaceAutomationsPanel, SurfacePanelCopyOrTransferPost, shouldEditCommentsInModal, isApp, surfaceStore, xInfo, xWallQuotaExceeded, xExtensionRequired, fileThatExceededSize, xRemakePanelOld, isNativeMenu, xPermitNotification, xReloadNotification, isFirstView, xSearchBar, user, isMobileLayout, xNewComposerModalPanel, canUseSections, xReportPanel, xDevelopersPanel, xSettingsPanel, hasSidePanelOutsideSurface, xEditBookmarksDialog, xDraftsPanel, isPickingLocation, xContributorsList, postUnderActionCid, xTransferPostPanel, postBeingTransferredCid, xCopyPostPanel, postBeingCopiedCid, xExpandedPostPanel, xFilterModal, xUserMenu, xGuestIdCard, xSurfaceSharePanel, xQrModal, xRemakePanel, xLibraryUpgradeModal, xUpgradeModal, commentUnderActionId, sectionUnderActionId, xSectionRenameModal, xConnectionCancellation, xDisconnectionCancellation, isSmallerThanTabletPortrait, xActivityPanel, isContainerSmallerThanTabletPortrait, isContainerSmallerThanTabletLandscape, xSurfaceFreezePanel, xSurfaceDetailsPanel, xSurfaceAutomationsPanel, originalReactionData, postIdBeingReactedTo, amIRegistered, isDemoPadletPanelAndShouldHideUI, isAppOpeningNativeLocationPicker, xCurtain, xCommentMenu, hideExtensionRequired, hideWallQuotaExceeded, hideInfo, hidePermitNotification, hideReloadNotification, endPostTransfer, postTransferNavigate, endPostCopy, postCopyNavigate, closeLibraryUpgradeModal, showSettingsPanel, closePlanUpgradeModal, navigateCopyPanel, navigateTransferPanel, hideCanvasConnection, dir, isAppUsing, isUsingMediaComments }
}

})